import { useCallback, useMemo } from 'react'
import { useWorkspace } from '../context/useWorkspace'
import { useToast } from '../context/useToast'
import {
  DEFAULT_WORKSPACE_SURFACE_MASK,
  isSurfaceAllowed,
  type WorkspaceSurfaceMask,
  type WorkspaceSurfaceKey,
} from '../types/workspaceSurfaceMask'
import type { WorkspaceAction } from '../utils/workspacePermissions'

const PREVIEW_BLOCK_LOG_KEY = 'timelining-preview-blocked-actions'

/**
 * Central guard for preview (demo/public) workspaces.
 *
 * `isPreview` is true whenever the active workspace is a synthetic preview.
 * `block(actionLabel)` fires a toast telling the visitor that mutation isn't
 * available and offering a graduation CTA. It also increments a
 * blocked-action counter in localStorage for conversion telemetry (P6).
 */
export const PREVIEW_GRADUATION_EVENT = 'timelining:preview-graduate'

export const requestPreviewGraduation = () => {
  window.dispatchEvent(new CustomEvent(PREVIEW_GRADUATION_EVENT))
}

export const usePreviewGuard = () => {
  const { workspaces, activeWorkspaceId } = useWorkspace()
  const { showToast } = useToast()
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )
  const isPreview = Boolean(activeWorkspace?.isPreview)
  const previewKind = activeWorkspace?.previewKind ?? null
  const previewSource = activeWorkspace?.previewSource ?? null
  const mask: WorkspaceSurfaceMask = activeWorkspace?.previewSurfaceMask ?? DEFAULT_WORKSPACE_SURFACE_MASK

  const isSurfaceVisible = useCallback(
    (surface: WorkspaceSurfaceKey) => {
      if (!isPreview) return true
      return isSurfaceAllowed(mask, surface)
    },
    [isPreview, mask],
  )

  const isSensitiveVisible = useCallback(
    (action: Extract<WorkspaceAction, `readonly.view_${string}`>) => {
      if (!isPreview) return true
      return mask.sensitive[action] === true
    },
    [isPreview, mask],
  )

  const previewWorkspaceId = activeWorkspace?.id ?? 'unknown'

  const block = useCallback(
    (actionLabel: string) => {
      if (!isPreview) return false
      try {
        const raw = window.localStorage.getItem(PREVIEW_BLOCK_LOG_KEY)
        const parsed = raw ? JSON.parse(raw) : {}
        const workspaceId = previewWorkspaceId
        parsed[workspaceId] = (parsed[workspaceId] ?? 0) + 1
        window.localStorage.setItem(PREVIEW_BLOCK_LOG_KEY, JSON.stringify(parsed))
        window.dispatchEvent(new CustomEvent('timelining:preview-blocked', { detail: { workspaceId, actionLabel } }))
      } catch {
        // ignore telemetry write failures
      }
      const message = previewKind === 'DEMO'
        ? `You're exploring the demo — switch to your workspace to ${actionLabel}.`
        : `This is a public preview — request access to ${actionLabel}.`
      showToast(message, 'info', previewKind === 'DEMO'
        ? { label: 'Switch to my workspace', run: requestPreviewGraduation }
        : undefined)
      return true
    },
    [isPreview, previewKind, previewWorkspaceId, showToast],
  )

  return {
    isPreview,
    previewKind,
    previewSource,
    mask,
    activeWorkspace,
    isSurfaceVisible,
    isSensitiveVisible,
    block,
  } as const
}

export const readBlockedActionCounts = (): Record<string, number> => {
  try {
    const raw = window.localStorage.getItem(PREVIEW_BLOCK_LOG_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}
